import useGeneratorStore from '@/state/generator-store';
import { cn } from '@/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';

interface DurationInputProps {
  className?: string;
  theme?: 'dark' | 'light';
  initialValue?: number;
  disabled?: boolean;
}

const convertSecondsToTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    hours: hours.toString().padStart(2, '0'),
    minutes: minutes.toString().padStart(2, '0'),
    seconds: seconds.toString().padStart(2, '0'),
  };
};

const DurationInput: React.FC<DurationInputProps> = ({ className, theme = 'light', initialValue = 60, disabled = false }) => {
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('01');
  const [seconds, setSeconds] = useState('00');

  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const secondsRef = useRef<HTMLInputElement>(null);

  const { register, control, formValues, generatorForm, isGeneratorDrawerShow } = useGeneratorStore((state) => ({
    register: state.register,
    control: state.control,
    formValues: state.formValues,
    generatorForm: state.generatorForm,
    isGeneratorDrawerShow: state.isGeneratorDrawerShow,
  }));

  const {
    field: { onChange },
  } = useController({
    name: 'duration',
    control,
    defaultValue: formValues?.duration,
  });

  const duration = useWatch({
    control,
    name: 'duration',
    defaultValue: 0,
  });

  useEffect(() => {
    if (duration && isGeneratorDrawerShow) {
      const { hours, minutes, seconds } = convertSecondsToTime(duration);
      setHours(hours);
      setMinutes(minutes);
      setSeconds(seconds);
    }
  }, [isGeneratorDrawerShow]);

  useEffect(() => {
    register('duration');
  }, [register]);

  useEffect(() => {
    if (initialValue) {
      const integerValue = Math.trunc(initialValue);
      const { hours, minutes, seconds } = convertSecondsToTime(integerValue);
      setHours(hours);
      setMinutes(minutes);
      setSeconds(seconds);
      onChange(integerValue);
      setValue?.('duration', integerValue);
    }
  }, [initialValue]);

  const { setValue } = generatorForm ?? {};

  const handleChange = (value: string, setter: React.Dispatch<React.SetStateAction<string>>, max: number) => {
    let numValue = parseInt(value, 10);
    if (isNaN(numValue)) numValue = 0;
    if (numValue > max) numValue = max;
    const formattedValue = numValue.toString().padStart(2, '0');
    setter(formattedValue);
    let totalSeconds = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
    if (totalSeconds < 5) {
      totalSeconds = 5;
      const { hours: minHours, minutes: minMinutes, seconds: minSeconds } = convertSecondsToTime(totalSeconds);
      setHours(minHours);
      setMinutes(minMinutes);
      setSeconds(minSeconds);
    }
  };

  const handleIncrement = (name: string, value: string, setter: React.Dispatch<React.SetStateAction<string>>, max: number) => {
    let numValue = parseInt(value, 10);
    const step = name === 'minutes' ? 1 : 5;
    numValue = Math.min(max, numValue + step);
    const formattedValue = numValue.toString().padStart(2, '0');
    setter(formattedValue);
    let newTotal = 0;
    if (name === 'minutes') {
      newTotal = Number(hours) * 3600 + numValue * 60 + Number(seconds);
    } else if (name === 'seconds') {
      newTotal = Number(hours) * 3600 + Number(minutes) * 60 + numValue;
    }
    onChange(newTotal);
    setValue?.('duration', newTotal);
  };

  const handleDecrement = (name: string, value: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    let numValue = parseInt(value, 10);
    const step = name === 'minutes' ? 1 : 5;
    numValue = Math.max(0, numValue - step);
    const formattedValue = numValue.toString().padStart(2, '0');
    setter(formattedValue);

    let newTotal = 0;
    if (name === 'minutes') {
      newTotal = Number(hours) * 3600 + numValue * 60 + Number(seconds);
    } else if (name === 'seconds') {
      newTotal = Number(hours) * 3600 + Number(minutes) * 60 + numValue;
    }
    if (newTotal < 5) {
      newTotal = 5;
      const { hours: minHours, minutes: minMinutes, seconds: minSeconds } = convertSecondsToTime(newTotal);
      setHours(minHours);
      setMinutes(minMinutes);
      setSeconds(minSeconds);
    }
    onChange(newTotal);
    setValue?.('duration', newTotal);
  };

  const handleBlur = (value: string, setter: React.Dispatch<React.SetStateAction<string>>, max: number) => {
    let numValue = parseInt(value, 10);
    if (isNaN(numValue)) numValue = 0;

    if (setter === setSeconds) {
      numValue = Math.ceil(numValue / 5) * 5;
      if (numValue >= max) {
        numValue = 0;
        const newMinutes = Number(minutes) + 1;
        const newSeconds = 0;
        setSeconds(newSeconds.toString().padStart(2, '0'));
        setMinutes(newMinutes.toString().padStart(2, '0'));
        const totalSeconds = Number(hours) * 3600 + Number(newMinutes) * 60 + numValue;
        onChange(totalSeconds);
        setValue?.('duration', totalSeconds);
        return;
      }
      const formattedValue = numValue.toString().padStart(2, '0');
      setter(formattedValue);
      const totalSeconds = Number(hours) * 3600 + Number(minutes) * 60 + numValue;
      onChange(totalSeconds);
      setValue?.('duration', totalSeconds);
    }
    const totalSeconds = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
    onChange(totalSeconds);
    setValue?.('duration', totalSeconds);
  };

  return (
    <div
      className={cn('inline-flex bg-gray-900 py-2 px-1 rounded-lg relative w-fit', className)}
      style={{
        background: theme === 'light' ? 'linear-gradient(#191A1B, #1E2021)' : 'linear-gradient(rgb(25, 26, 27), rgb(30, 32, 33))',
        border: '1px solid #474A4C',
        boxShadow: 'inset 2px 2px 12px rgba(0, 0, 0, 1)',
      }}
    >
      <div className={`flex items-center ${theme === 'light' ? 'bg-white' : 'bg-[#1d1b1e]'} rounded-jen-spacing-4 py-1 px-2 w-fit`}>
        <div
          className=" text-white font-thin text-[0.7rem] bg-black rounded-sm font-pressura-mono py-3 px-1"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          DURATION
        </div>
        <div className="flex">
          {[
            {
              name: 'hours',
              value: hours,
              setter: setHours,
              ref: hoursRef,
              max: 99,
              disabled: true || disabled,
            },
            {
              name: 'minutes',
              value: minutes,
              setter: setMinutes,
              ref: minutesRef,
              max: 15,
              disabled: disabled,
            },
            {
              name: 'seconds',
              value: seconds,
              setter: setSeconds,
              ref: secondsRef,
              max: 59,
              disabled: disabled,
            },
          ].map((item, index) => (
            <div key={index} className="flex">
              <div key={index} className="flex flex-col items-center">
                <button
                  onClick={() => handleIncrement(item.name, item.value, item.setter, item.max)}
                  className={`text-gray-400 rounded-sm ${!item.disabled ? (theme === 'light' ? 'hover:bg-black' : 'hover:bg-white') : ''} w-full transition duration-200 text-[0.8rem] ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  type="button"
                  disabled={item.disabled}
                >
                  ▲
                </button>
                <input
                  ref={item.ref}
                  type="text"
                  value={item.value}
                  onChange={(e) => handleChange(e.target.value, item.setter, item.max)}
                  className={`w-16 text-center text-[2.4rem] ${theme === 'light' ? 'bg-white text-black' : 'bg-[#1d1b1e] text-white'}  ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''} `}
                  style={{ lineHeight: 'normal' }}
                  disabled={item.disabled}
                  onBlur={(e) => handleBlur(e.target.value, item.setter, item.max)}
                />
                <button
                  onClick={() => handleDecrement(item.name, item.value, item.setter)}
                  className={`text-gray-400 rounded-sm ${!item.disabled ? (theme === 'light' ? 'hover:bg-black' : 'hover:bg-white') : ''} w-full transition duration-100 text-[0.8rem] ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  type="button"
                  disabled={item.disabled}
                >
                  ▼
                </button>
              </div>
              {item.name !== 'seconds' && <span className="flex items-center justify-center font-bold text-[1.2rem]">:</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DurationInput;
